import { useEffect, useState } from "react";
import { PlusCircle, X } from "lucide-react";
import Switch from "../Switch/Switch";
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import { InputV2 } from "../InputV2/InputV2";

type Props = {
  title: string;
  label?: string;
  onUpdate: (data: { enabled: boolean; emails: string[] }) => void;
  initialEmails: string[];
  initialIsEnabled: boolean;
  isDisabled: boolean;
};

function AlertEmailField({
  title = "",
  label = "",
  onUpdate,
  initialEmails = [],
  initialIsEnabled = false,
  isDisabled = false,
}: Props) {
  const [emails, setEmails] = useState(initialEmails);
  const [enabled, setEnabled] = useState(initialIsEnabled);

  useEffect(() => {
    setEmails(initialEmails);
  }, [initialEmails]);

  useEffect(() => {
    setEnabled(initialIsEnabled);
  }, [initialIsEnabled]);

  const handleEmailChange = (newEmails: string[]) => {
    setEmails(newEmails);
    onUpdate({ enabled, emails: newEmails });
  };

  const handleSwitchChange = () => {
    setEnabled((prev) => {
      const newEnabled = !prev;
      onUpdate({ enabled: newEnabled, emails });
      return newEnabled;
    });
  };

  const handleAddEmail = (email: string) => {
    if (email && !emails.includes(email)) {
      handleEmailChange([...emails, email]);
    }
  };

  const handleRemoveEmail = (email: string) => {
    handleEmailChange(emails.filter((e) => e !== email));
  };

  return (
    <div className="p-2 rounded-lg bg-background">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        <Switch checked={enabled} onChange={handleSwitchChange} />
      </div>
      {enabled && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2 mb-2">
            {emails.map((email, index) => (
              <div
                key={index}
                className="bg-secondary text-secondary-foreground px-2 py-1 rounded-full text-sm flex items-center"
              >
                {email}
                <ButtonV2
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 ml-1 hover:bg-secondary-foreground hover:text-secondary rounded-full"
                  onClick={() => handleRemoveEmail(email)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove email</span>
                </ButtonV2>
              </div>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <InputV2
              type="email"
              placeholder={`${label}`}
              onKeyDown={(e: any) => {
                if (e.key === "Enter" && e.currentTarget.value) {
                  handleAddEmail(e.currentTarget.value);
                  e.currentTarget.value = "";
                }
              }}
              disabled={isDisabled}
            />
            <ButtonV2
              size="icon"
              onClick={(e: any) => {
                const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                if (input.value) {
                  handleAddEmail(input.value);
                  input.value = "";
                }
              }}
              disabled={isDisabled}
            >
              <PlusCircle className="h-4 w-4" />
              <span className="sr-only">Add email</span>
            </ButtonV2>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlertEmailField;